<script setup>
import { ref, watch, onMounted, computed } from 'vue'
import { useAddressStore } from '~/stores/addresses.js'
import { useConveyancingCaseStore } from '~/stores/conveyancing-cases.js'
import { useFormStore } from '~/stores/forms.js'
import BasicAutocompleteInput from '~/components/custom-formkit/BasicAutocompleteInput.vue'

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
})

const conveyancingCaseStore = useConveyancingCaseStore()

const inputValue = ref(props.context.value)
const selectedAddress = ref(props.context.value && props.context.value['selected_address'])
const fullAddress = ref(props.context.value && props.context.value['full_address'])
const udprn = ref(props.context.value && props.context.value['udprn'])
const uprn = ref(props.context.value && props.context.value['uprn'])
const line_1 = ref(props.context.value && props.context.value['line_1'])
const line_2 = ref(props.context.value && props.context.value['line_2'])
const line_3 = ref(props.context.value && props.context.value['line_3'])
const postcode = ref(props.context.value && props.context.value['postcode'])
const options = ref([])
const disabled = computed(() => props.context.disabled || false)

const formStoreForOptional = useFormStore()
const fieldsRequired = computed(() => {
  const path = props.context.attrs?.optionalWhenPath
  if (!path) return true
  const expected = props.context.attrs?.optionalWhenValue ?? 'yes'
  return formStoreForOptional?.payload?.[path] !== expected
})

// When the schema passes a revalidateOnPathPrefix, fire a re-input on this
// node whenever any payload key starting with that prefix changes. This is
// how cross-section dependencies (e.g. matrimonial dispute affecting whether
// the address is required) keep the validation verdict fresh.
watch(
  () => {
    const prefix = props.context?.attrs?.revalidateOnPathPrefix
    if (!prefix) return ''
    const payload = formStoreForOptional?.payload || {}
    return Object.keys(payload)
      .filter((k) => k.startsWith(prefix))
      .sort()
      .map((k) => `${k}=${JSON.stringify(payload[k])}`)
      .join('|')
  },
  () => {
    const node = props.context?.node
    if (!node) return
    // Re-assigning the validation prop forces FormKit to re-parse the rules
    // and re-run validation against the current value, even when the value
    // itself is unchanged. node.input(node.value) short-circuits on no-ops.
    const v = node.props?.validation
    if (v !== undefined) {
      node.props.validation = ''
      node.props.validation = v
    }
  },
)
const postcodeError = ref('')

const formatPostcode = () => {
  if (!postcode.value) return
  let cleanedPostcode = postcode.value.toUpperCase().replace(/\s+/g, '')
  const ukPostcodeRegex =
    /^(GIR0AA|[A-PR-UWYZ]([0-9]{1,2}|[A-HK-Y][0-9]{1,2}|[A-HK-Y][0-9][A-HJKS-UW]|[0-9][A-HJKPS-UW]|[A-HK-Y][0-9][ABEHMNPRV-Y]|[A-HK-Y][A-HK-Y][0-9][ABEHMNPRV-Y])([0-9][ABD-HJLNP-UW-Z]{2}))$/i
  if (ukPostcodeRegex.test(cleanedPostcode)) {
    postcode.value = cleanedPostcode.replace(/(.+)(.{3})$/, '$1 $2')
    postcodeError.value = ''
  } else {
    postcodeError.value = 'Please enter a valid UK postcode.'
  }
}

watch(
  inputValue,
  (newVal) => {
    props.context.node.input(newVal)
  },
  { deep: true, immediate: true },
)

const handleSelect = (item) => {
  if (item) {
    selectedAddress.value = item['address']
    udprn.value = item['udprn']

    // Parse address string to populate line_1 and line_2
    // For "26 Holburn Road, Aberdeen, AB10" → line_1: "26 Holburn Road", line_2: "Aberdeen"
    // For "Flat 1, 26 Holburn Road, Aberdeen, AB10" → line_1: "Flat 1, 26 Holburn Road", line_2: "Aberdeen"
    if (item['town_or_city'] && item['address']) {
      line_2.value = item['town_or_city']

      // Find the town/city in the address string and take everything before it as line_1
      const townCity = item['town_or_city']
      const address = item['address']
      const addressParts = address.split(',').map((part) => part.trim())

      // Find the index of the part that matches the town/city
      const townCityIndex = addressParts.findIndex((part) => part === townCity)

      if (townCityIndex > 0) {
        // Take all parts before the town/city and join them with commas
        line_1.value = addressParts.slice(0, townCityIndex).join(', ')
      } else if (addressParts.length > 0) {
        // Fallback: if town/city not found in parts, take first part
        line_1.value = addressParts[0]
      }
    }

    // Fetch UPRN and complete postcode from detailed endpoint
    updateAddressByUdprn(udprn.value)
  }
}

const handleSelectBlur = () => {
  if (!store.response.some((option) => option.address === selectedAddress.value)) {
    clearInput()
  }
}

const clearInput = () => {
  inputValue.value = ''
  fullAddress.value = ''
  udprn.value = ''
  uprn.value = ''
  line_1.value = ''
  line_2.value = ''
  line_3.value = ''
  postcode.value = ''
}

const amendAddress = () => {
  ;(udprn.value = ''), (uprn.value = ''), updateInputValue()
}

const updateInputValue = () => {
  inputValue.value = {
    ...inputValue.value,
    selected_address: selectedAddress.value,
    udprn: udprn.value,
    uprn: uprn.value,
    line_1: line_1.value,
    line_2: line_2.value,
    line_3: line_3.value,
    postcode: postcode.value,
    full_address: updateFullAddress(),
  }
}

const updateFullAddress = () => {
  let addressParts = [line_1.value, line_2.value, line_3.value, postcode.value]

  fullAddress.value = addressParts.filter((part) => part && part.trim() !== '').join(', ')

  return fullAddress.value
}

const store = useAddressStore()

const updateAddressByUdprn = async (udprnSearch) => {
  const address = await store.getAddressesUdprn(udprnSearch)
  if (address) {
    // Get UPRN and complete postcode from detailed endpoint
    uprn.value = address.uprn
    postcode.value = address.postcode

    // Only use line_3 from detailed endpoint (line_1 and line_2 already populated from address-finder)
    line_3.value = address.line_3

    // Fallback: if line_1 or line_2 are still empty, use detailed endpoint data
    if (!line_1.value && address.line_1) {
      line_1.value = address.line_1
    }
    if (!line_2.value && address.line_2) {
      line_2.value = address.line_2
    }

    updateInputValue()
  }
}

const fetchSuggestions = async (queryString) => {
  await store.fetchAddresses(queryString)
  return store.response
}
</script>

<template>
  <div>
    <el-row>
      <el-col :span="24">
        <el-autocomplete
          v-model="selectedAddress"
          :fetch-suggestions="fetchSuggestions"
          placeholder="Type to search address ..."
          class="w-full"
          value-key="address"
          required="required"
          clearable="true"
          :disabled="disabled"
          @select="handleSelect"
          @blur="handleSelectBlur"
        ></el-autocomplete>
      </el-col>
    </el-row>
    <el-row gutter="20">
      <el-col :xs="24" :sm="12" class="mt-4">
        <el-form-item label="Address Line 1" :required="fieldsRequired">
          <el-input v-model="line_1" :disabled="disabled" @change="amendAddress" />
        </el-form-item>
      </el-col>
      <el-col :xs="24" :sm="12" class="mt-4">
        <el-form-item label="Address Line 2" :required="fieldsRequired">
          <el-input v-model="line_2" :disabled="disabled" @change="amendAddress" />
        </el-form-item>
      </el-col>
      <el-col :xs="24" :sm="12" class="mt-4">
        <el-form-item label="Address Line 3">
          <el-input v-model="line_3" :disabled="disabled" @change="amendAddress" />
        </el-form-item>
      </el-col>
      <el-col :xs="24" :sm="12" class="mt-4">
        <el-form-item label="Postcode" :required="fieldsRequired" :error="postcodeError">
          <el-input v-model="postcode" :disabled="disabled" @change="amendAddress" @blur="formatPostcode" />
        </el-form-item>
      </el-col>
    </el-row>
  </div>
</template>
